import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X, Check, Send } from "lucide-react";
import { Button, Field, Input, Textarea } from "@/design/components";
import { useSubmitProjectEditRequest } from "@/domain/useProjectEditRequest";

// ZEUS-130/144 — contractor/subcontractor requests an edit to a PUBLISHED
// project. Never applies directly: submitting flips the project to
// `review` and stratus_admin must approve or reject (see
// PendingEditRequestPanel). Deliberately labeled as a request throughout —
// mirrors RequestProjectCreationModal's "never call it Create/Edit
// outright" convention, so it can't be mistaken for the direct-edit flow
// (EditProjectModal, stratus_admin only).
//
// Real-API only — no demo-mode simulation yet (see useProjectEditRequest.ts's
// file-level note). The caller is responsible for not rendering the trigger
// in demo mode.

export function RequestProjectEditModal({
  open,
  onOpenChange,
  projectId,
  currentValues,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  currentValues: {
    name: string;
    description?: string;
    region?: string;
    district?: string;
    budgeted?: number;
  };
}) {
  const { submitProjectEditRequest, isPending, error } = useSubmitProjectEditRequest();

  const [name, setName] = useState(currentValues.name);
  const [description, setDescription] = useState(currentValues.description ?? "");
  const [region, setRegion] = useState(currentValues.region ?? "");
  const [district, setDistrict] = useState(currentValues.district ?? "");
  const [budgeted, setBudgeted] = useState(currentValues.budgeted != null ? String(currentValues.budgeted) : "");
  const [justification, setJustification] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(currentValues.name);
    setDescription(currentValues.description ?? "");
    setRegion(currentValues.region ?? "");
    setDistrict(currentValues.district ?? "");
    setBudgeted(currentValues.budgeted != null ? String(currentValues.budgeted) : "");
    setJustification("");
    setSubmitted(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId]);

  // Only the fields the contractor actually changed go into the request —
  // matches the backend's "at least one changed field" validation and
  // means the diff shown to oversight (PendingEditRequestPanel) is exactly
  // what's proposed, not every field re-sent unchanged.
  function buildChanges() {
    const changes: Record<string, string | number> = {};
    if (name.trim() !== currentValues.name) changes.name = name.trim();
    if (description.trim() !== (currentValues.description ?? "")) changes.description = description.trim();
    if (region.trim() !== (currentValues.region ?? "")) changes.region = region.trim();
    if (district.trim() !== (currentValues.district ?? "")) changes.district = district.trim();
    const budgetedNum = budgeted.trim() ? Number(budgeted.trim()) : undefined;
    if (budgetedNum !== currentValues.budgeted && budgeted.trim()) changes.budgeted = budgetedNum as number;
    return changes;
  }

  const changes = buildChanges();
  const canSubmit = Object.keys(changes).length > 0 && justification.trim().length > 0;

  function reset() {
    setName(currentValues.name);
    setDescription(currentValues.description ?? "");
    setRegion(currentValues.region ?? "");
    setDistrict(currentValues.district ?? "");
    setBudgeted(currentValues.budgeted != null ? String(currentValues.budgeted) : "");
    setJustification("");
    setSubmitted(false);
  }

  async function onSubmit() {
    if (!canSubmit) return;
    const result = await submitProjectEditRequest(projectId, {
      justification: justification.trim(),
      changes,
    });
    if (result) setSubmitted(true);
  }

  return (
    <Dialog.Root open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-ink/25 backdrop-blur-[2px] animate-fade-in" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 flex max-h-[88vh] w-full max-w-md -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-card border border-line bg-card shadow-overlay animate-fade-in">
          <div className="flex items-start justify-between gap-3 p-6 pb-3">
            <Dialog.Title className="text-base font-semibold tracking-tight text-ink">
              {submitted ? "Edit submitted" : "Request project edit"}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button aria-label="Close" className="rounded-md p-1 text-faint hover:bg-subtle hover:text-ink">
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>

          {submitted ? (
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 pb-6">
              <div className="flex items-start gap-3 rounded-md border border-ok/30 bg-okSoft px-3 py-3 text-sm text-ok">
                <Check className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  Your proposed edit has been sent to Stratus for review. The project moves to
                  Review until it's approved or rejected.
                </span>
              </div>
              <div className="flex justify-end gap-2">
                <Button onClick={() => onOpenChange(false)}>Done</Button>
              </div>
            </div>
          ) : (
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 pb-4">
              <Dialog.Description className="text-sm leading-relaxed text-mute">
                This is a request, not a direct edit — Stratus reviews it and either approves
                (applying your changes) or rejects it with a reason.
              </Dialog.Description>

              <Field label="Name">
                <Input value={name} onChange={(e) => setName(e.target.value)} />
              </Field>

              <Field label="Description">
                <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Region">
                  <Input value={region} onChange={(e) => setRegion(e.target.value)} />
                </Field>
                <Field label="District">
                  <Input value={district} onChange={(e) => setDistrict(e.target.value)} />
                </Field>
              </div>

              <Field label="Budget, UGX">
                <Input type="number" value={budgeted} onChange={(e) => setBudgeted(e.target.value)} />
              </Field>

              <Field label="Justification">
                <Textarea
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  rows={3}
                  placeholder="Why is this edit necessary?"
                />
              </Field>

              {error && <p role="alert" className="text-sm text-bad">{error}</p>}
            </div>
          )}

          {!submitted && (
            <div className="flex items-center justify-end gap-2 border-t border-line p-6 pt-4">
              <Dialog.Close asChild>
                <Button variant="ghost">Cancel</Button>
              </Dialog.Close>
              <Button disabled={!canSubmit || isPending} onClick={onSubmit}>
                <Send className="h-4 w-4" /> {isPending ? "Submitting…" : "Submit request"}
              </Button>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
